import React, { useEffect, useMemo, useState } from "react";
import { useBetContext } from "../../../../../context/BetContext.tsx";
import { Odd } from "../../../../../models/Odd_response_model.tsx";
import { BetDetails, PlaceWagersResult } from "../../../../../models/MyBetModel.tsx";
import { IoMdClose } from "react-icons/io";
import { IoCalendarOutline, IoClipboardOutline } from "react-icons/io5";
import { useUser } from "../../../../../context/UserContext.tsx";
import { logUserActivity } from "../../../../../services/player/LoginService.tsx";
import { Link } from "react-router-dom";
import {
    applyNewOddToBet,
    formatNumber,
    formatSpecialtyMarketLabel,
    getBetPrimaryName,
    getBetSelectionLabel,
    getBetSpecialtyMarketLabel,
    isLiveBet,
} from "../../../../../helpers/functions.tsx";
import { placeApiAllWagers } from "../../../../../services/player/apis/Schelude.tsx";
import { useModal } from "../../../../../context/ModalContext.tsx";
import BetLogo from "../../BetLogo.tsx";
import "../../../../../styles/StraightQuickSlip.css";

interface OneBetsResponsiveProps {
    setShowLimitAlert?: (value: boolean) => void;
    setErrorMessage?: (value: string) => void;
}

function formatConfirmLine(
    odd?: Odd | { points?: number | null; price?: number | null; selection_line?: string | null; market_id?: string | null } | null,
    marketName?: string | null,
) {
    if (!odd) return "";
    const isMoneyline =
        String(marketName ?? "").toLowerCase().includes("moneyline") ||
        String(odd.market_id ?? "").toLowerCase() === "moneyline";
    const price = odd.price != null ? formatNumber(odd.price) : "";
    if (isMoneyline || odd.points == null) return price;
    const line = String(odd.selection_line ?? "").toLowerCase();
    const points = formatNumber(odd.points, undefined, "points");
    if (line === "over" || line.startsWith("over")) return `O ${odd.points} (${price})`.trim();
    if (line === "under" || line.startsWith("under")) return `U ${odd.points} (${price})`.trim();
    return `${points} (${price})`;
}

const OneBet = (props: OneBetsResponsiveProps) => {

    const { setShowLimitAlert, setErrorMessage } = props;
    const { user, updateUser, userIpData } = useUser();
    const { bets, updateBet, clearBets, updateBetLive, useFreePlay, setUseFreePlay } = useBetContext();
    const bet = useMemo(() => (bets && bets.length > 0 ? bets[0] : null), [bets]);
    const [stake, setStake] = useState(bet?.bet);
    const [odd, setOdd] = useState(bet?.odd);
    const [profit, setProfit] = useState(bet?.reward ?? 0)
    const [isPlacing, setIsPlacing] = useState(false);
    const [isPlaced, setIsPlaced] = useState(false);
    const [ticketNumber, setTicketNumber] = useState<number | null>(null);
    const [oddsImproved, setOddsImproved] = useState<boolean | null>(null);
    const [showOddChange, setShowOddChange] = useState(false);
    const [newOddData, setNewOddData] = useState<Odd | null>(null);
    const [autoAcceptOdds, setAutoAcceptOdds] = useState(false);
    const { closeModal } = useModal();
    const isLive = isLiveBet(bet);
    const specialtyMarketLabel = getBetSpecialtyMarketLabel(bet);
    

    useEffect(() => {
        if (!bet || isPlaced) return;
        setStake(bet.bet ?? 0);
        setOdd(bet.odd);
        setProfit(bet.reward ?? 0);
    }, [bet, isPlaced]);

    const _calcProfit = (stake: number, odd?: Odd) => {
        const price = odd?.price ?? bet?.odd?.price;
        if (price === undefined) return 0;

        let profit = 0;
        if (price < 0) profit = (stake * 100) / Math.abs(price);
        else profit = stake * (price / 100);

        return Math.round(profit);
    };

    const updateStake = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        if (value === "" || !odd) {
            setStake(0);
            setProfit(0);
            if (bet) {
                updateBet({ ...bet, bet: 0, reward: 0, odd });
            }
            return;
        }

        const newStake = Math.round(parseFloat(value));
        const newProfit = Math.round(_calcProfit(newStake, odd));

        setStake(newStake);
        setProfit(newProfit);

        if (bet) {
            updateBet({
                ...bet,
                bet: newStake,
                reward: newProfit,
                odd,
                betName: odd.name,
                id_selection: bet.id_selection,
            });
        }
    };

    const updateProfit = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        if (value === "" || !odd) {
            setStake(0);
            setProfit(0);
            if (bet) {
                updateBet({ ...bet, bet: 0, reward: 0, odd });
            }
            return;
        }

        const newProfit = Math.round(parseFloat(value));

        let newStake = 0;

        if (odd.price > 0) {
            newStake = Math.round(newProfit / (odd.price / 100));
        } else if (odd.price < 0) {
            newStake = Math.round((newProfit * Math.abs(odd.price)) / 100);
        }

        setProfit(newProfit);
        setStake(newStake);

        if (bet) {
            updateBet({
                ...bet,
                bet: newStake,
                reward: newProfit,
                odd,
                betName: odd.name,
                id_selection: bet.id_selection,
            });
        }
    };

    const confirmedBet = async () => {
        setIsPlacing(true);

        try {
            if (!user || !user.id || !user.parent || !bet) return;

            const userId = user.id;
            let currentOdd = odd;
            let currentStake = stake ?? 0;
            let currentProfit = profit ?? 0;
            const maxAttempts = autoAcceptOdds ? 3 : 1;

            for (let attempt = 0; attempt < maxAttempts; attempt++) {
                const betAmount = parseFloat(Number(currentStake).toFixed(2));
                const rewardAmount = parseFloat(Number(currentProfit).toFixed(2));

                const { odds, newOdd, ...rest } = bet as any;
                const betToPlace = {
                    ...rest,
                    bet: betAmount,
                    reward: rewardAmount,
                    odd: currentOdd,
                    betName: currentOdd?.name ?? bet.betName,
                    id_selection: currentOdd?.id ?? bet.id_selection,
                };

                const result: PlaceWagersResult = await placeApiAllWagers(userId, [betToPlace], useFreePlay);

                if (result?.errors?.[0]?.error?.errorType === "oddChange") {
                    const changed = result.changedBets?.[0];
                    if (autoAcceptOdds && attempt < maxAttempts - 1 && changed?.newOdd) {
                        const updated = applyNewOddToBet(
                            { ...bet, bet: currentStake, reward: currentProfit, odd: currentOdd },
                            changed.newOdd
                        );
                        currentOdd = changed.newOdd as Odd;
                        currentStake = updated.bet ?? currentStake;
                        currentProfit = updated.reward ?? currentProfit;

                        setOdd(currentOdd);
                        setStake(currentStake);
                        setProfit(currentProfit);
                        setOddsImproved(!!(updated as any).oddsImproved);
                        updateBetLive({
                            ...bet,
                            bet: currentStake,
                            reward: currentProfit,
                            odd: currentOdd,
                            betName: currentOdd.name,
                            id_selection: currentOdd.id,
                        });
                        setShowOddChange(false);
                        setNewOddData(null);
                        continue;
                    }

                    if (changed?.newOdd) {
                        setNewOddData(changed.newOdd as Odd);
                        setShowOddChange(true);
                    }
                    return;
                }

                if (result?.hasErrors) {
                    const firstError = result?.errors?.[0]?.error;
                    const message = firstError?.message
                        ? `${firstError.message}`
                        : 'There was an error placing one or more bets.';

                    setShowLimitAlert?.(true);
                    setErrorMessage?.(message);
                    return;
                }

                const placedBet = (result?.bets?.length ? result.bets[0] : betToPlace) as BetDetails;
                const totalStake = Number(placedBet.bet ?? betAmount);
                const ticketFromResult = placedBet.ticket ?? null;

                setStake(totalStake);
                setProfit(Number(placedBet.reward ?? rewardAmount));
                setTicketNumber(
                    ticketFromResult
                        ? Number(String(ticketFromResult).split('-')[0]) || null
                        : null
                );

                if (useFreePlay) {
                    updateUser({
                        freePlay: parseFloat(((user.freePlay ?? 0) - totalStake).toFixed(2)),
                    });
                } else {
                    updateUser({
                        available: parseFloat(((user.available ?? 0) - totalStake).toFixed(2)),
                    });
                }

                logUserActivity(
                    user.userName,
                    `Ticket: ${ticketFromResult ?? ''} Risk: $${Number(placedBet.bet ?? betAmount).toFixed(2)} Win: $${Number(placedBet.reward ?? rewardAmount).toFixed(2)}`,
                    userIpData
                );

                setIsPlaced(true);
                setUseFreePlay?.(false);
                return;
            }

        } catch (error) {
            console.error(error);
            setShowLimitAlert?.(true);
            setErrorMessage?.('There was an error confirming the bet. Please try again.');
        } finally {
            setIsPlacing(false);
        }
    };

    const acceptNewOdds = () => {
        if (!bet || !newOddData) return;

        const updated = applyNewOddToBet(
            { ...bet, bet: stake ?? 0, reward: profit ?? 0, odd },
            newOddData
        );

        setOddsImproved(!!(updated as any).oddsImproved);
        setOdd(newOddData);
        setStake(updated.bet);
        setProfit(updated.reward);

        updateBetLive({
            ...bet,
            bet: updated.bet,
            reward: updated.reward,
            odd: newOddData,
            betName: newOddData.name,
            id_selection: newOddData.id,
        });

        setShowOddChange(false);
        setNewOddData(null);
        setErrorMessage?.('');
    };

    const resetBetState = () => {
        setIsPlaced(false);
        setIsPlacing(false);
        setTicketNumber(null);
        clearBets();
        setUseFreePlay?.(false);
        setOddsImproved(null);
        closeModal();
    };


    if (!user || !bet) return null;

    const pickName = getBetPrimaryName(bet);
    const marketLabel =
        specialtyMarketLabel
        || formatSpecialtyMarketLabel(bet.marketName || bet.odd?.market);
    const scheduledLabel = bet?.gameDate?.toDate().toLocaleString("en-US") ?? "";
    const selectionLabel = getBetSelectionLabel(bet);

    return (
        <div className="sq-slip sq-confirm">
            <div className="sq-slip__header">
                <div className="sq-slip__header-left">
                    <span className="sq-slip__count">1</span>
                    <h1 className="sq-slip__title">{!isPlaced ? "Confirm Your Bet" : "Wager Confirmed"}</h1>
                </div>
                <button type="button" className="sq-slip__close" onClick={resetBetState} aria-label="Close">
                    <IoMdClose size={25} />
                </button>
            </div>

            <div className="sq-slip__body">
                <div className="sq-confirm__card">
                    <div className="sq-slip__pick">
                        <BetLogo bet={bet} className="sport-logos-modal" />
                        <div className="sq-slip__pick-meta">
                            <span className={`sq-slip__pick-name ${oddsImproved === null ? "" : oddsImproved ? "odds-up" : "odds-down"}`}>
                                {pickName}
                            </span>
                            {marketLabel ? <span className="sq-slip__pick-market">{marketLabel}</span> : null}
                            {bet?.gameType?.toLowerCase().includes("live") && (
                                <span className="fw-bold inter live-bet-label">
                                    Live
                                    <span className="live-text-dot" aria-hidden="true" />
                                </span>
                            )}
                        </div>
                        {odd ? (
                            <div className="sq-slip__odds">
                                <span className="sq-confirm__odds">
                                    {formatConfirmLine(odd, bet?.marketName)}
                                </span>
                            </div>
                        ) : null}
                    </div>

                    <div className="sq-confirm__meta">
                        {scheduledLabel ? (
                            <div className="sq-confirm__meta-row">
                                <IoCalendarOutline className="sq-confirm__meta-icon" aria-hidden="true" />
                                <span>
                                    <span className="sq-confirm__meta-label">Scheduled</span>
                                    <span className="sq-confirm__meta-value">{scheduledLabel}</span>
                                </span>
                            </div>
                        ) : null}
                        {selectionLabel ? (
                            <div className="sq-confirm__meta-row">
                                <IoClipboardOutline className="sq-confirm__meta-icon" aria-hidden="true" />
                                <span>
                                    <span className="sq-confirm__meta-label">Selection</span>
                                    <span className="sq-confirm__meta-value">{selectionLabel}</span>
                                </span>
                            </div>
                        ) : null}
                    </div>
                </div>

                {!isPlaced && showOddChange && newOddData && (
                    <div className="odds-changed-alert">
                        <div className="odds-changed-card">
                            <div className="odds-changed-header">
                                <span className="odds-changed-icon">!</span>
                                <div className="odds-changed-titles">
                                    <h4 className="odds-changed-title">Odds have changed</h4>
                                    <p className="odds-changed-subtitle">
                                        The odds for this selection were updated. Review the new values and accept to continue.
                                    </p>
                                </div>
                            </div>

                            <ul className="odds-changed-list">
                                <li className="odds-changed-item">
                                    <span className="odds-changed-bet-name">
                                        {bet?.betName ?? "Selection"}
                                    </span>
                                    <div className="odds-changed-row">
                                        <span className="odds-old">
                                            {formatConfirmLine(odd, bet?.marketName)}
                                        </span>
                                        <span className="odds-arrow">→</span>
                                        <span className={`odds-new ${(newOddData.price ?? 0) > (odd?.price ?? 0) ? "odds-up" : "odds-down"}`}>
                                            {formatConfirmLine(newOddData, bet?.marketName)}
                                        </span>
                                    </div>
                                </li>
                            </ul>

                            <div className="odds-changed-actions">
                                <button type="button" className="odds-changed-btn-cancel" onClick={() => { setNewOddData(null); setShowOddChange(false); }}>
                                    Cancel
                                </button>
                                <button type="button" className="odds-changed-btn-accept" onClick={acceptNewOdds}>
                                    Accept New Odds
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="sq-confirm__totals">
                    <div className="sq-confirm__total sq-confirm__total--risk">
                        <span className="sq-confirm__total-label">Risking</span>
                        <div className="sq-confirm__total-value">
                            <span>$</span>
                            <input
                                type="number"
                                step="any"
                                inputMode="decimal"
                                min="0"
                                onChange={updateStake}
                                value={stake || ""}
                                readOnly={isPlaced}
                            />
                        </div>
                    </div>
                    <div className="sq-confirm__total sq-confirm__total--win">
                        <span className="sq-confirm__total-label">To Win</span>
                        <div className="sq-confirm__total-value">
                            <span>$</span>
                            <input
                                type="number"
                                step="any"
                                min="0"
                                inputMode="decimal"
                                value={profit || ""}
                                onChange={updateProfit}
                                readOnly={isPlaced}
                            />
                        </div>
                    </div>
                </div>

                {!isPlaced ? (
                    <>
                        {isLive && (
                            <label className="sq-slip__freeplay">
                                <input
                                    type="checkbox"
                                    className="checkbox-pitcher"
                                    checked={autoAcceptOdds}
                                    onChange={() => setAutoAcceptOdds(prev => !prev)}
                                />
                                <span>Automatically accept updated odds</span>
                            </label>
                        )}
                        <button
                            type="button"
                            className="sq-confirm__place"
                            onClick={confirmedBet}
                            disabled={isPlacing || showOddChange}
                        >
                            {isPlacing ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Please wait a moment...
                                </>
                            ) : (
                                "Place Wager"
                            )}
                        </button>
                    </>
                ) : (
                    <>
                        <div className="d-flex justify-content-center">
                            <span className="ticket">Ticket: {ticketNumber}</span>
                        </div>
                        <div className="sq-slip__actions">
                            <Link to="/bets" className="sq-slip__btn sq-slip__btn--back" onClick={() => { resetBetState(); }}>
                                Pending
                            </Link>
                            <button type="button" className="sq-slip__btn sq-slip__btn--confirm" onClick={resetBetState}>
                                Continue
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default OneBet;
